"use client";

import { FormEvent, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";

type LoginResponse = {
  token: string;
  user: {
    id: string;
    nome?: string | null;
    cognome?: string | null;
    email: string;
    role: string;
  };
};

type GoogleCredentialResponse = {
  credential: string;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: Record<string, string>
          ) => void;
        };
      };
    };
  }
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  borderRadius: "8px",
  border: "1px solid var(--border-default)",
  padding: "9px 12px",
  fontSize: "14px",
  color: "var(--text-primary)",
  background: "var(--bg-surface)",
  outline: "none",
  transition: "border-color 150ms",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "13px",
  fontWeight: 500,
  color: "var(--text-secondary)",
  marginBottom: "6px",
};

export default function Home() {
  const router = useRouter();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [message, setMessage] = useState<string>("");
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const googleBtnRef = useRef<HTMLDivElement | null>(null);
  const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

  const saveSession = (payload: LoginResponse) => {
    localStorage.setItem("crm_token", payload.token);
    localStorage.setItem("crm_user", JSON.stringify(payload.user));
    setToken(payload.token);
    setIsError(false);
    setMessage(`Accesso effettuato come ${payload.user.email}`);
    router.push("/dashboard");
  };

  const loginWithCredential = async (credential: string) => {
    if (!credential) {
      setIsError(true);
      setMessage("Token Google non ricevuto. Riprova.");
      return;
    }

    setIsLoading(true);
    setMessage("");
    try {
      const response = await fetch("/api/auth/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential }),
      });
      const text = await response.text();
      const data = text ? (JSON.parse(text) as LoginResponse | { error?: string }) : {};
      if (!response.ok) {
        const parsedError =
          typeof data === "object" && data !== null && "error" in data
            ? (data as { error?: string }).error
            : undefined;
        throw new Error(
          parsedError ||
            "Accesso Google non autorizzato. Verifica che l'utente sia abilitato nel CRM."
        );
      }
      saveSession(data as LoginResponse);
    } catch (error) {
      setIsError(true);
      setMessage(
        error instanceof Error
          ? error.message
          : "Errore login Google. Verifica i dati e riprova."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocalLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!username.trim()) {
      setIsError(true);
      setMessage("Inserisci lo username.");
      return;
    }
    if (!password.trim()) {
      setIsError(true);
      setMessage("Inserisci la password.");
      return;
    }

    setIsLoading(true);
    setMessage("");
    try {
      const response = await fetch("/api/auth/login-local", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const text = await response.text();
      const data = text ? (JSON.parse(text) as LoginResponse | { error?: string }) : {};
      if (!response.ok) {
        const parsedError =
          typeof data === "object" && data !== null && "error" in data
            ? (data as { error?: string }).error
            : undefined;
        throw new Error(parsedError || "Credenziali non valide.");
      }
      saveSession(data as LoginResponse);
    } catch (error) {
      setIsError(true);
      setMessage(
        error instanceof Error
          ? error.message
          : "Errore login locale. Verifica i campi e riprova."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const verifyToken = async () => {
    const savedToken = localStorage.getItem("crm_token");
    if (!savedToken) {
      setIsError(true);
      setMessage("Nessun token salvato.");
      return;
    }
    setIsLoading(true);
    setMessage("");
    try {
      const response = await fetch("/api/me", {
        headers: { Authorization: `Bearer ${savedToken}` },
      });
      const text = await response.text();
      const data = text ? (JSON.parse(text) as { error?: string }) : {};
      if (!response.ok) {
        throw new Error(data.error || "Token non valido");
      }
      setIsError(false);
      setMessage("Token valido. Endpoint /api/me raggiunto con successo.");
    } catch (error) {
      setIsError(true);
      setMessage(error instanceof Error ? error.message : "Errore inatteso");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const savedToken = localStorage.getItem("crm_token");
    if (!savedToken) return;
    // Valida il token prima di fare il redirect — evita il loop se scaduto
    fetch("/api/me", { headers: { Authorization: `Bearer ${savedToken}` } })
      .then((res) => {
        if (res.ok) {
          setToken(savedToken);
          router.push("/dashboard");
        } else {
          localStorage.removeItem("crm_token");
          localStorage.removeItem("crm_user");
        }
      })
      .catch(() => {
        // Errore di rete: mantieni il token, non redirigere
      });
  }, [router]);

  useEffect(() => {
    if (!googleClientId || !googleBtnRef.current) {
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.google || !googleBtnRef.current) {
        setIsError(true);
        setMessage("Google SSO non disponibile al momento. Ricarica la pagina.");
        return;
      }
      try {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: (response: GoogleCredentialResponse) => {
            if (!response.credential) {
              setIsError(true);
              setMessage("Token Google non valido o mancante. Riprova.");
              return;
            }
            loginWithCredential(response.credential);
          },
        });
        googleBtnRef.current.innerHTML = "";
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: "outline",
          size: "large",
          text: "signin_with",
        });
      } catch {
        setIsError(true);
        setMessage(
          "Configurazione Google SSO non valida. Verifica GOOGLE_CLIENT_ID e domini autorizzati."
        );
      }
    };
    script.onerror = () => {
      setIsError(true);
      setMessage("Impossibile caricare Google SSO. Verifica connessione e configurazione.");
    };

    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [googleClientId]);

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg-base)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
    }}>
      <div style={{ width: "100%", maxWidth: "400px" }}>

        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            background: "var(--brand-500)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 14px",
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h1 style={{ fontSize: "22px", fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>
            Init-S CRM
          </h1>
          <p style={{ fontSize: "14px", color: "var(--text-secondary)", marginTop: "4px" }}>
            Accedi al tuo account
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: "var(--bg-surface)",
          borderRadius: "12px",
          border: "1px solid var(--border-default)",
          padding: "28px",
          boxShadow: "var(--shadow-sm)",
        }}>
          {/* Login form */}
          <form onSubmit={handleLocalLogin} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <label htmlFor="username" style={labelStyle}>Username</label>
              <input
                id="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                style={inputStyle}
                autoComplete="username"
                placeholder="Inserisci username"
                required
                suppressHydrationWarning
                onFocus={(e) => { e.currentTarget.style.borderColor = "var(--brand-500)"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border-default)"; }}
              />
            </div>
            <div>
              <label htmlFor="password" style={labelStyle}>Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                style={inputStyle}
                autoComplete="current-password"
                placeholder="••••••••"
                required
                suppressHydrationWarning
                onFocus={(e) => { e.currentTarget.style.borderColor = "var(--brand-500)"; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border-default)"; }}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "10px 16px",
                borderRadius: "8px",
                background: isLoading ? "var(--brand-600)" : "var(--brand-500)",
                color: "white",
                border: "none",
                fontSize: "14px",
                fontWeight: 600,
                cursor: isLoading ? "not-allowed" : "pointer",
                opacity: isLoading ? 0.75 : 1,
                transition: "background 150ms, opacity 150ms",
              }}
              onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.background = "var(--brand-600)"; }}
              onMouseLeave={(e) => { if (!isLoading) e.currentTarget.style.background = "var(--brand-500)"; }}
            >
              {isLoading ? "Accesso in corso…" : "Accedi"}
            </button>
          </form>

          {/* Divider SSO */}
          {googleClientId && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "20px 0" }}>
                <div style={{ flex: 1, height: "1px", background: "var(--border-default)" }} />
                <span style={{ fontSize: "12px", color: "var(--text-muted)", whiteSpace: "nowrap" }}>oppure continua con</span>
                <div style={{ flex: 1, height: "1px", background: "var(--border-default)" }} />
              </div>
              <div ref={googleBtnRef} style={{ display: "flex", justifyContent: "center" }} />
            </>
          )}

          {!googleClientId && (
            <div style={{
              marginTop: "20px",
              padding: "10px 12px",
              borderRadius: "8px",
              background: "var(--amber-50)",
              border: "1px solid var(--amber-100, #FDE68A)",
              fontSize: "13px",
              color: "var(--amber-600)",
            }}>
              Google SSO non disponibile: NEXT_PUBLIC_GOOGLE_CLIENT_ID non configurata.
            </div>
          )}

          {/* Feedback */}
          {message && (
            <div style={{
              marginTop: "16px",
              padding: "10px 12px",
              borderRadius: "8px",
              background: isError ? "var(--red-50)" : "var(--green-50)",
              border: `1px solid ${isError ? "var(--red-100, #FECACA)" : "var(--green-100, #C6F0D8)"}`,
              fontSize: "13px",
              color: isError ? "var(--red-600)" : "var(--green-600)",
            }}>
              {message}
            </div>
          )}
        </div>

        {/* Debug token verify */}
        <div style={{ textAlign: "center", marginTop: "14px" }}>
          <button
            type="button"
            onClick={verifyToken}
            disabled={isLoading}
            style={{
              background: "none",
              border: "none",
              fontSize: "12px",
              color: "var(--text-muted)",
              cursor: "pointer",
              padding: "4px 8px",
              transition: "color 150ms",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text-secondary)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; }}
          >
            Verifica token /api/me
          </button>
        </div>

        {token && (
          <p style={{ marginTop: "8px", fontSize: "11px", color: "var(--text-muted)", wordBreak: "break-all", textAlign: "center" }}>
            Token: {token.substring(0, 40)}…
          </p>
        )}
      </div>
    </div>
  );
}
